import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Dropdown } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import { useLocation } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import useCallbackRef from 'hooks/useCallbackRef';
import { SearchForm } from 'components/ResolveHeader/components/SearchForm';
import { getIsArrayEmpty } from 'utils';
import { SEARCH_ROUTE } from 'data/global';
import SearchContext from 'providers/Search/Search.context';
import useRecentSearches from 'providers/Search/hooks/useRecentSearches';
import RecentSearchesList from './components/RecentSearchesList';
import useResizeObserver from 'hooks/useResizeObserver';

import './SearchFormWithRecentSearches.styles.scss';

type SearchFormWithRecentSearchesProps = {
  className?: string;
};

const SearchFormWithRecentSearches: FC<SearchFormWithRecentSearchesProps> = (
  props,
) => {
  const { className } = props;

  const {
    isSearching,
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
  } = useContext(SearchContext);

  const recentSearches = useRecentSearches();

  const { setQueryParam, setParamsAndNavigate } = useQueryParams();

  const { pathname } = useLocation();

  const currentRouteIsSearch = useMemo(
    () => pathname === SEARCH_ROUTE,
    [pathname],
  );

  const [triggerWidth, setTriggerWidth] = useState<number>();

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();
  const [triggerEl, triggerRef] = useCallbackRef<HTMLDivElement>();

  const isEmptySearches = getIsArrayEmpty(recentSearches);

  const updateDropdownVisibility = useCallback(
    (value: string) => {
      if (isEmptySearches) return;

      dropdown?.setIsOpen(!value.trim());
    },
    [isEmptySearches, dropdown],
  );

  const onFocus = useCallback(
    (e) => updateDropdownVisibility((e.target as HTMLInputElement).value),
    [updateDropdownVisibility],
  );

  const onBlur = useCallback(() => dropdown?.setIsOpen(false), [dropdown]);

  const onSelectRecentSearch = useCallback(
    (term: string) => {
      if (currentRouteIsSearch) {
        setQueryParam('searchString', term);
      } else {
        setParamsAndNavigate(SEARCH_ROUTE, { searchString: term });
      }

      addRecentSearch(term);
      dropdown?.setIsOpen(false);
    },
    [
      currentRouteIsSearch,
      dropdown,
      setQueryParam,
      setParamsAndNavigate,
      addRecentSearch,
    ],
  );

  const updateTriggerWidth = useCallback(
    () => setTriggerWidth(triggerEl?.clientWidth),
    [triggerEl],
  );

  useEffect(() => {
    if (isEmptySearches) {
      dropdown?.setIsOpen(false);
      return;
    }

    const input = triggerEl?.querySelector('input');

    if (document.activeElement === input && !input?.value.trim())
      dropdown?.setIsOpen(true);
  }, [isEmptySearches, dropdown, triggerEl]);

  useResizeObserver(triggerEl, updateTriggerWidth);

  return (
    <Dropdown
      ref={dropdownRef}
      disabled
      onBlur={onBlur}
      onFocus={onFocus}
      className={classNames('search-form-with-recent-searches', className)}
      portalClassName="search-form-with-recent-searches__portal"
      renderAsPortal
      trigger={
        <div ref={triggerRef}>
          <SearchForm
            isLoading={isSearching}
            onChange={updateDropdownVisibility}
          />
        </div>
      }
    >
      <div style={{ width: triggerWidth }}>
        <RecentSearchesList
          searches={recentSearches}
          onSelect={onSelectRecentSearch}
          onRemove={removeRecentSearch}
          onClearAll={clearRecentSearches}
        />
      </div>
    </Dropdown>
  );
};

export default SearchFormWithRecentSearches;
